import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Users, MessageSquare, MessageSquareReply, Timer, AlertTriangle, Radio, ArrowRight, ShieldAlert } from "lucide-react";
import { useQ, fmtDuration, fmtTime } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Stat, LiveBadge, Meter, AgentAvatar } from "@/components/common";

const useTicker = (base, live) => {
  const [secs, setSecs] = useState(base || 0);
  useEffect(() => {
    setSecs(base || 0);
    if (!live) return;
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [base, live]);
  return secs;
};

const LiveHero = ({ ov }) => {
  const { t } = useT();
  const live = ov.live_stream;
  const secs = useTicker(live?.elapsed_seconds, !!live);
  return (
    <Panel className={`lg:col-span-2 h-full fade-up ${live ? "beam" : ""}`} data-testid="stream-status-card">
      <div className="flex items-center justify-between">
        <p className="eyebrow">{t("streaming_status")}</p>
        <LiveBadge live={!!live} />
      </div>
      {live ? (
        <div className="mt-6 flex items-center gap-5">
          <AgentAvatar agent={ov.active_agent} size="h-16 w-16" speaking />
          <div className="min-w-0">
            <p className="font-display text-2xl font-bold truncate" data-testid="live-stream-title">{live.title}</p>
            <p className="text-sm text-slate-400">{ov.active_agent?.name} · {t(`mode_${live.mode}`)} · <span className="font-mono-ui">{fmtDuration(secs)}</span></p>
          </div>
          <Link to={`/dashboard/streams/${live.id}`} className="ml-auto shrink-0 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold hover:bg-indigo-500 transition-colors" data-testid="open-live-room-btn">
            {t("open_room")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="mt-6">
          <p className="font-display text-2xl font-bold">{t("no_live")}</p>
          <p className="text-sm text-slate-400 mt-1">{t("no_live_hint")}</p>
          <Link to="/dashboard/streams" className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5 transition-colors" data-testid="go-streams-btn">
            <Radio className="h-4 w-4" /> {t("go_streams")}
          </Link>
        </div>
      )}
    </Panel>
  );
};

const Events = ({ events }) => {
  const { t } = useT();
  const color = { error: "text-rose-400", warning: "text-amber-400", info: "text-emerald-400" };
  return (
    <Panel className="lg:col-span-2 h-full" data-testid="events-panel">
      <p className="eyebrow mb-4">{t("recent_events")}</p>
      {events.length === 0 ? <p className="text-sm text-slate-500">{t("no_events")}</p> : (
        <ul className="space-y-3">
          {events.map((e) => (
            <li key={e.id} className="flex gap-3 text-sm">
              <span className="font-mono-ui text-xs text-slate-500 w-12 shrink-0 pt-0.5">{fmtTime(e.created_at)}</span>
              <span className={`${color[e.level]} shrink-0`}>●</span>
              <span className="text-slate-300">{e.message}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

export default function Overview() {
  const { t } = useT();
  const { data: ov } = useQ("/overview", { refetchInterval: 5000 });
  if (!ov) return <div className="h-64 rounded-lg bg-white/[0.03] animate-pulse" />;
  const l = ov.limits;
  return (
    <div>
      <PageHeader eyebrow={t("nav_overview")} title={t("overview_title")} subtitle={t("overview_sub")} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <LiveHero ov={ov} />
        <Stat label={t("viewers")} value={ov.viewers} icon={Users} testId="stat-viewers" hint={t("viewers_hint")} delay={60} />
        <Stat label={t("errors")} value={ov.errors} icon={AlertTriangle} testId="stat-errors" accent={ov.errors ? "text-rose-400" : "text-slate-50"} delay={120} />
        <Stat label={t("msgs_received")} value={ov.messages_received} icon={MessageSquare} testId="stat-received" delay={160} />
        <Stat label={t("msgs_answered")} value={ov.messages_answered} icon={MessageSquareReply} testId="stat-answered" accent="text-indigo-300" delay={200} />
        <Stat label={t("msgs_blocked")} value={ov.messages_blocked} icon={ShieldAlert} testId="stat-blocked" delay={240} />
        <Stat label={t("hours_month")} value={fmtDuration(ov.usage.stream_seconds || 0)} icon={Timer} testId="stat-duration" delay={280} />
        <Events events={ov.events} />
        <Panel className="lg:col-span-2 h-full space-y-5" data-testid="usage-panel">
          <div className="flex items-center justify-between">
            <p className="eyebrow">{t("plan_usage")}</p>
            <Link to="/dashboard/billing" className="text-xs text-indigo-300 hover:text-indigo-200 uppercase tracking-wider" data-testid="usage-plan-link">{ov.plan}</Link>
          </div>
          <Meter label={t("ai_agents")} used={ov.agents} total={l.agents} testId="meter-agents" />
          <Meter label={t("ai_responses_mo")} used={ov.usage.ai_messages || 0} total={l.ai_messages} testId="meter-ai" />
          <Meter label={t("stream_hours_mo")} used={Math.round((ov.usage.stream_seconds || 0) / 360) / 10} total={l.stream_hours} testId="meter-hours" />
        </Panel>
      </div>
    </div>
  );
}
